<template>
  <app-page ref="pageRoot" class="saved-report-workspace" labelledby="saved-report-title" full-width>
    <page-header
      title-id="saved-report-title"
      :title="pageTitle"
      :description="pageDescription"
    >
      <template #status>
        <q-badge outline :color="statusBadge.color">{{ statusBadge.label }}</q-badge>
      </template>
      <template #metadata>
        <span v-if="normalized">
          {{ normalized.document.name }}
          <template v-if="normalized.document.dateDisplay"> · {{ normalized.document.dateDisplay }}</template>
          <template v-if="isHistorical"> · история №{{ historyId }}</template>
        </span>
        <span v-else>Сохраненный отчет организации</span>
      </template>
      <template #actions>
        <q-btn flat no-caps color="primary" icon="arrow_back" label="К списку" :to="{ name: 'reports_list' }" />
        <q-btn
          v-if="mode === 'read' && normalized"
          outline
          no-caps
          color="primary"
          icon="picture_as_pdf"
          label="Печатная форма"
          :to="printRoute"
          data-testid="legacy-report-print-route"
        />
        <q-btn
          v-if="mode === 'read' && canEdit"
          unelevated
          no-caps
          color="primary"
          icon="edit"
          label="Редактировать"
          :to="editRoute"
        />
        <q-btn
          v-if="mode !== 'read' && normalized"
          outline
          no-caps
          color="primary"
          icon="description"
          label="К карточке"
          :to="cardRoute"
        />
      </template>
    </page-header>

    <app-state
      v-if="pageState !== 'ready'"
      :state="pageState"
      :title="stateTitle"
      :message="stateMessage"
      :action-to="pageState === 'not-found' ? { name: 'reports_list' } : null"
      @retry="load"
    />

    <template v-else>
      <q-banner v-if="isHistorical" dense class="saved-report-workspace__notice bg-info text-white">
        Открыта сохраненная версия истории №{{ historyId }}. Ее данные доступны только для чтения.
      </q-banner>
      <q-banner v-else-if="!normalized.document.isActive" dense class="saved-report-workspace__notice bg-warning text-dark">
        Отчет находится в архиве. Редактирование недоступно.
      </q-banner>
      <q-banner v-if="normalized.unknownFields.length" dense class="saved-report-workspace__notice bg-grey-2 text-dark">
        Дополнительные поля документа ({{ normalized.unknownFields.length }}) сохранены и будут переданы без изменений.
      </q-banner>

      <template v-if="mode === 'print'">
        <page-section
          title="PDF отчета"
          description="Печатная форма привязана к выбранной версии. Интерфейс не изменяет PDF."
        >
          <report-artifact-actions
            :document-id="documentId"
            :org-id="currentOrgId"
            :history-id="artifactHistoryId"
            :disabled="offline || !canReadFinance"
            stacked
          />
        </page-section>
      </template>

      <template v-else-if="mode === 'edit'">
        <q-banner v-if="!canEdit" dense class="saved-report-workspace__notice bg-warning text-dark">
          {{ editUnavailableReason }}
        </q-banner>
        <legacy-report-editor
          :body="workingBody"
          :columns="normalized.valueColumns"
          :date-inputs="dateInputs"
          :disabled="!canEdit || actionState === 'saving'"
          :rows="rowDefinitions"
          @update-body-field="updateBodyField"
          @update-date="updateDate"
          @update-row-value="updateRowValue"
        />
        <sticky-action-bar
          :state="actionState"
          :dirty="isDirty && canEdit"
          :error-message="saveError"
          primary-test-id="legacy-report-save"
          @cancel="reset"
          @primary="save"
        />
      </template>

      <template v-else>
        <page-section title="Реквизиты" description="Снимок реквизитов выбранной версии отчета.">
          <dl class="saved-report-workspace__metadata-grid">
            <div>
              <dt>Номер</dt>
              <dd>{{ workingBody.in_org_number }}</dd>
            </div>
            <div>
              <dt>Дата документа</dt>
              <dd>{{ dateInputs.document_date }}</dd>
            </div>
            <div>
              <dt>Отчетная дата</dt>
              <dd>{{ dateInputs.date_current }}</dd>
            </div>
            <div>
              <dt>Единица измерения</dt>
              <dd>{{ measureLabel }}</dd>
            </div>
            <div>
              <dt>Организация</dt>
              <dd>{{ workingBody.organisation_name }}</dd>
            </div>
            <div>
              <dt>ИНН</dt>
              <dd>{{ workingBody.organisation_inn }}</dd>
            </div>
          </dl>
        </page-section>

        <page-section
          title="Показатели"
          description="Данные показаны из сохраненной версии отчета."
        >
          <report-table
            :rows="normalized.rows"
            :value-columns="normalized.valueColumns"
            :measure-label="normalized.measureLabel"
          />
        </page-section>

        <page-section
          title="PDF отчета"
          :description="artifactDescription"
        >
          <report-artifact-actions
            :document-id="documentId"
            :org-id="currentOrgId"
            :history-id="artifactHistoryId"
            :disabled="offline || !canReadFinance"
            stacked
          />
        </page-section>
      </template>

      <page-section
        v-if="mode !== 'print'"
        title="История изменений"
        description="Каждая версия открывается как отдельный неизменяемый снимок и передается в PDF тем же history id."
      >
        <q-list bordered separator class="saved-report-workspace__history" data-testid="legacy-report-history">
          <q-item
            clickable
            :active="!isHistorical"
            :to="historyRoute('last')"
          >
            <q-item-section>
              <q-item-label>Текущая версия</q-item-label>
              <q-item-label caption>Последнее сохраненное состояние отчета</q-item-label>
            </q-item-section>
            <q-item-section side><q-icon name="chevron_right" /></q-item-section>
          </q-item>
          <q-item
            v-for="change in normalized.document.changes"
            :key="change.id"
            clickable
            :active="String(change.id) === String(historyId)"
            :to="historyRoute(change.id)"
          >
            <q-item-section>
              <q-item-label>{{ change.user || 'Пользователь' }}</q-item-label>
              <q-item-label caption>{{ change.message || `Версия №${change.id}` }}</q-item-label>
            </q-item-section>
            <q-item-section side top>
              <q-item-label caption>{{ change.date || '' }}</q-item-label>
            </q-item-section>
            <q-item-section side><q-icon name="chevron_right" /></q-item-section>
          </q-item>
        </q-list>
      </page-section>
    </template>

    <confirm-dialog
      :model-value="leaveDialogOpen"
      title="Выйти без сохранения?"
      body="Несохраненные изменения отчета будут потеряны."
      confirm-label="Выйти без сохранения"
      cancel-label="Остаться"
      destructive
      @cancel="cancelDirtyLeave"
      @confirm="confirmDirtyLeave"
      @update:model-value="handleLeaveDialogModel"
    />
  </app-page>
</template>

<script>
import moment from 'moment'
import {
  AppPage,
  AppState,
  ConfirmDialog,
  PageHeader,
  PageSection,
  StickyActionBar
} from '../../../components/ui'
import ReportArtifactActions from '../../../components/documents/ReportArtifactActions.vue'
import LegacyReportEditor from './components/LegacyReportEditor.vue'
import ReportTable from './components/ReportTable.vue'
import { getLegacyReportDefinition, getReportByCode } from './utils/reportMetadata'
import {
  buildLegacyReportSaveBody,
  legacyDateInput,
  LegacyReportAdapterError,
  normalizeLegacyReportDocument
} from './utils/reportNormalizer'

const clone = (value) => JSON.parse(JSON.stringify(value))
const same = (left, right) => JSON.stringify(left) === JSON.stringify(right)

export default {
  name: 'SavedReportWorkspace',
  components: {
    AppPage,
    AppState,
    ConfirmDialog,
    LegacyReportEditor,
    PageHeader,
    PageSection,
    ReportArtifactActions,
    ReportTable,
    StickyActionBar
  },
  props: {
    reportCode: { type: String, required: true },
    mode: {
      type: String,
      default: 'read',
      validator: (value) => ['read', 'edit', 'print'].includes(value)
    },
    documentId: { type: [String, Number], default: null },
    historyId: { type: [String, Number], default: 'last' }
  },
  data() {
    return {
      pageState: 'loading',
      stateMessage: '',
      normalized: null,
      workingBody: {},
      savedBody: {},
      dateInputs: {},
      savedDateInputs: {},
      actionState: 'idle',
      saveError: '',
      loadRequestId: '',
      saveRequestId: '',
      pendingSaveBody: null,
      eventHandlers: [],
      leaveDialogOpen: false,
      pendingNavigation: null
    }
  },
  computed: {
    legacyDefinition() {
      return getLegacyReportDefinition(this.reportCode)
    },
    canonicalReportCode() {
      return this.legacyDefinition ? this.legacyDefinition.reportCode : this.reportCode
    },
    report() {
      return getReportByCode(this.canonicalReportCode)
    },
    pageTitle() {
      if (this.report) return this.report.name
      return this.mode === 'print' ? 'Печатная форма отчета' : 'Сохраненный отчет'
    },
    pageDescription() {
      if (this.mode === 'edit') return 'Редактирование сохраненного отчета в едином рабочем пространстве.'
      if (this.mode === 'print') return 'Просмотр и загрузка готового PDF.'
      return this.report ? this.report.description : 'Просмотр сохраненной версии отчета.'
    },
    stateTitle() {
      if (this.pageState === 'not-found') return 'Отчет не найден'
      if (this.pageState === 'access-denied') return 'Недостаточно прав'
      if (this.pageState === 'loading') return 'Загружаем отчет'
      return 'Не удалось загрузить отчет'
    },
    currentOrgId() {
      return this.connection && this.connection.org_id
    },
    permissions() {
      return this.$store.state.permissions || {}
    },
    canReadFinance() {
      return this.permissions.open_fin_documents === true || this.permissions.edit_fin_documents === true
    },
    canWriteFinance() {
      return this.permissions.edit_fin_documents === true
    },
    offline() {
      return this.$store.state.connection_status === 'offline'
    },
    isHistorical() {
      return this.historyId !== null && this.historyId !== undefined &&
        this.historyId !== '' && String(this.historyId) !== 'last'
    },
    canEdit() {
      return this.mode === 'edit' && !this.isHistorical && !this.offline &&
        this.canWriteFinance && Boolean(this.normalized && this.normalized.document.isActive)
    },
    editUnavailableReason() {
      if (this.offline) return 'Нет подключения. Сохранение будет доступно после восстановления связи.'
      if (!this.canWriteFinance) return 'У текущей роли нет права редактировать бухгалтерские документы.'
      if (this.isHistorical) return 'Историческая версия доступна только для чтения.'
      return 'Архивный отчет доступен только для чтения.'
    },
    isDirty() {
      return !same(this.workingBody, this.savedBody) || !same(this.dateInputs, this.savedDateInputs)
    },
    statusBadge() {
      if (this.pageState === 'loading') return { color: 'info', label: 'Загрузка' }
      if (this.pageState === 'error' || this.actionState === 'error') return { color: 'negative', label: 'Ошибка' }
      if (this.isHistorical) return { color: 'info', label: `История №${this.historyId}` }
      if (this.normalized && !this.normalized.document.isActive) return { color: 'warning', label: 'В архиве' }
      if (this.actionState === 'saving') return { color: 'info', label: 'Сохранение' }
      if (this.actionState === 'saved') return { color: 'positive', label: 'Сохранено' }
      if (this.mode === 'edit' && this.isDirty) return { color: 'warning', label: 'Изменен' }
      return { color: 'positive', label: 'Сохранен' }
    },
    rowDefinitions() {
      return this.normalized ? this.normalized.rowDefinitions : []
    },
    measureLabel() {
      const value = this.workingBody.currency_measure
      return value && typeof value === 'object' ? (value.label || value.value || '—') : (value || '—')
    },
    artifactHistoryId() {
      return this.isHistorical ? this.historyId : 'last'
    },
    artifactDescription() {
      return this.isHistorical
        ? `PDF строится backend-ом по сохраненному снимку истории №${this.historyId}.`
        : 'PDF строится backend-ом по текущей сохраненной версии.'
    },
    cardRoute() {
      if (!this.legacyDefinition) return { name: 'reports_list' }
      return {
        name: this.legacyDefinition.infoRoute,
        params: { id: this.documentId, history_id: this.artifactHistoryId }
      }
    },
    editRoute() {
      if (!this.legacyDefinition) return { name: 'reports_list' }
      return { name: this.legacyDefinition.editRoute, params: { id: this.documentId } }
    },
    printRoute() {
      return {
        name: 'reports_print',
        params: { reportCode: this.canonicalReportCode },
        query: { id: this.documentId, history_id: this.artifactHistoryId }
      }
    }
  },
  watch: {
    documentId() {
      this.load()
    },
    historyId() {
      this.load()
    },
    reportCode() {
      this.load()
    }
  },
  mounted() {
    this.bind('didReceiveDocumentInfo', this.onLoaded)
    this.bind('onDocumentInfoError', this.onLoadError)
    this.bind('onSaveDocument', this.onSaved)
    if (this.connection.isAuthenticated) this.load()
    else this.bind('connectionReady', this.load)
  },
  beforeUnmount() {
    this.eventHandlers.forEach(([name, handler]) => this.emitter.off(name, handler))
  },
  methods: {
    bind(name, handler) {
      const bound = handler.bind(this)
      this.eventHandlers.push([name, bound])
      this.emitter.on(name, bound)
    },
    requestId(prefix) {
      return this.connection.makeRequestId(prefix)
    },
    historyRequestValue() {
      if (!this.isHistorical) return 'last'
      const value = String(this.historyId)
      return /^\d+$/.test(value) ? Number(value) : value
    },
    load() {
      if (!this.legacyDefinition || !this.documentId) {
        this.pageState = 'not-found'
        this.stateMessage = !this.legacyDefinition
          ? 'Тип сохраненного отчета не поддерживается.'
          : 'Для печатной формы не выбран сохраненный отчет.'
        return
      }
      this.pageState = 'loading'
      this.stateMessage = ''
      this.normalized = null
      this.loadRequestId = this.requestId('legacy-report-load')
      this.connection.getDocumentInfo(
        this.loadRequestId,
        this.documentId,
        this.historyRequestValue()
      )
    },
    onLoaded(response) {
      if (this.loadRequestId && response.request_id && response.request_id !== this.loadRequestId) return
      try {
        const normalized = normalizeLegacyReportDocument(
          this.canonicalReportCode,
          response.data,
          { historyId: this.artifactHistoryId }
        )
        this.normalized = normalized
        this.workingBody = clone(normalized.body)
        this.savedBody = clone(normalized.body)
        this.dateInputs = this.dateInputsFor(normalized.body)
        this.savedDateInputs = clone(this.dateInputs)
        this.pageState = 'ready'
        this.actionState = 'idle'
        this.saveError = ''
      } catch (error) {
        this.applyAdapterError(error)
      }
    },
    onLoadError(error) {
      this.pageState = error.state || 'error'
      this.stateMessage = error.message || 'Попробуйте повторить запрос.'
    },
    applyAdapterError(error) {
      if (error instanceof LegacyReportAdapterError) {
        this.pageState = error.state
        this.stateMessage = error.details && error.details.length
          ? `${error.message}. Поля: ${error.details.slice(0, 5).join(', ')}${error.details.length > 5 ? '…' : ''}`
          : error.message
        return
      }
      this.pageState = 'error'
      this.stateMessage = 'Не удалось обработать сохраненный отчет.'
    },
    dateInputsFor(body) {
      return {
        document_date: legacyDateInput(body.document_date),
        date_current: legacyDateInput(body.date_current),
        date_previous: legacyDateInput(body.date_previous),
        date_preprevious: legacyDateInput(body.date_preprevious)
      }
    },
    updateBodyField({ key, value }) {
      this.workingBody = { ...this.workingBody, [key]: value }
      this.clearActionFeedback()
    },
    updateDate({ key, value }) {
      this.dateInputs = { ...this.dateInputs, [key]: value }
      this.clearActionFeedback()
    },
    updateRowValue({ rowKey, field, value }) {
      this.workingBody = {
        ...this.workingBody,
        [rowKey]: { ...this.workingBody[rowKey], [field]: value }
      }
      this.clearActionFeedback()
    },
    clearActionFeedback() {
      if (['saved', 'error'].includes(this.actionState)) {
        this.actionState = 'idle'
        this.saveError = ''
      }
    },
    validDates() {
      return ['document_date', 'date_current', 'date_previous']
        .concat(this.dateInputs.date_preprevious ? ['date_preprevious'] : [])
        .every((key) => moment(this.dateInputs[key], 'DD.MM.YYYY', true).isValid())
    },
    save() {
      if (!this.canEdit || !this.isDirty || this.actionState === 'saving') return
      if (!this.validDates()) {
        this.actionState = 'error'
        this.saveError = 'Проверьте даты: требуется формат ДД.ММ.ГГГГ.'
        return
      }
      const body = buildLegacyReportSaveBody(this.workingBody, this.dateInputs, this.savedDateInputs)
      try {
        normalizeLegacyReportDocument(this.canonicalReportCode, {
          ...this.normalized.document,
          id: this.documentId,
          name: this.normalized.document.name,
          is_active: this.normalized.document.isActive,
          changes: this.normalized.document.changes,
          body
        })
      } catch (error) {
        this.actionState = 'error'
        this.saveError = error.message || 'Проверьте обязательные поля отчета.'
        return
      }
      this.pendingSaveBody = body
      this.saveRequestId = this.requestId('legacy-report-save')
      this.actionState = 'saving'
      this.saveError = ''
      this.connection.saveDocument(this.saveRequestId, body, this.documentId, 'report')
    },
    onSaved(response) {
      if (this.saveRequestId && response.request_id && response.request_id !== this.saveRequestId) return
      if (response.type === 'error' || response.error) {
        this.actionState = 'error'
        this.saveError = response.message || 'Не удалось сохранить отчет.'
        return
      }
      this.workingBody = clone(this.pendingSaveBody || this.workingBody)
      this.savedBody = clone(this.workingBody)
      this.savedDateInputs = clone(this.dateInputs)
      this.pendingSaveBody = null
      this.actionState = 'saved'
      this.$q.notify({ type: 'positive', message: 'Отчет сохранен' })
    },
    reset() {
      this.workingBody = clone(this.savedBody)
      this.dateInputs = clone(this.savedDateInputs)
      this.actionState = 'idle'
      this.saveError = ''
    },
    historyRoute(historyId) {
      if (!this.legacyDefinition) return { name: 'reports_list' }
      return {
        name: this.legacyDefinition.infoRoute,
        params: { id: this.documentId, history_id: String(historyId) }
      }
    },
    requestDirtyLeave(next) {
      this.pendingNavigation = next
      this.leaveDialogOpen = true
    },
    cancelDirtyLeave() {
      const next = this.pendingNavigation
      this.pendingNavigation = null
      this.leaveDialogOpen = false
      if (next) next(false)
    },
    confirmDirtyLeave() {
      const next = this.pendingNavigation
      this.pendingNavigation = null
      this.savedBody = clone(this.workingBody)
      this.savedDateInputs = clone(this.dateInputs)
      this.leaveDialogOpen = false
      if (next) next()
    },
    handleLeaveDialogModel(value) {
      if (!value && this.leaveDialogOpen) this.cancelDirtyLeave()
    }
  }
}
</script>

<style scoped>
.saved-report-workspace {
  min-width: 0;
  max-width: 100%;
  overflow-x: clip;
}

.saved-report-workspace__notice + .saved-report-workspace__notice {
  margin-top: var(--sid-space-2);
}

.saved-report-workspace__notice {
  margin-bottom: var(--sid-space-4);
}

.saved-report-workspace__metadata-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--sid-space-4);
  margin: 0;
}

.saved-report-workspace__metadata-grid div {
  min-width: 0;
}

.saved-report-workspace__metadata-grid dt {
  color: var(--sid-color-text-secondary);
  font-size: var(--sid-font-size-label);
}

.saved-report-workspace__metadata-grid dd {
  margin: var(--sid-space-1) 0 0;
  overflow-wrap: anywhere;
  font-weight: var(--sid-font-weight-medium);
}

.saved-report-workspace__history {
  overflow: hidden;
  border-radius: var(--sid-radius-surface);
}

@media (max-width: 767px) {
  .saved-report-workspace__metadata-grid {
    grid-template-columns: 1fr;
  }
}
</style>
